import homeApi from "@features/home/helpers/homeApi";
import { useAppContext } from "@context/app";
import { useCartContext } from "@context/cart";
import { modalActions } from "@context/modals";
import { useUI } from "@context/ui-managed-context";
import { COURSE_TYPE, DetailsExpire, EXPIRY_STATUS } from "@types";
import { delay } from "@utils/delay";
import { handleCourseUrl } from "@utils/handleCourseUrl";
import { FAVORITE_TYPE } from "enums/favorite-type";
import { MODAL_TYPES } from "types/modals";

export const useCardAction = () => {
  const { openAlert, setAlertView, alertView } = useUI();
  const {
    user: { isLoggedIn },
  } = useAppContext();
  const { addItem, removeItem } = useCartContext();

  const addFavoriteCourse = async (
    favorite: boolean,
    id: number,
    type: FAVORITE_TYPE = FAVORITE_TYPE.PRODUCT
  ): Promise<boolean> => {
    if (!isLoggedIn) {
      modalActions.addModal(MODAL_TYPES.NEW_LOGIN_ALERT, {
        config: {
          hideCloseIcon: true,
        },
      });
      return favorite;
    }

    const isAdding = !favorite;
    let updatedFavorite = isAdding;

    try {
      const apiMethod = isAdding
        ? "addFavoriteProduct"
        : "removeFavoriteProduct";
      const data = await homeApi[apiMethod](id, type).fetch();

      alertView.alertContent.push({
        mode: "danger",
        textBody: data.message,
        submitFunction: isAdding
          ? undefined
          : () => {
              addFavoriteCourse(!favorite, id);
              updatedFavorite = favorite;
            },
        duration: 2000,
      });

      setAlertView("SNACKBAR", alertView.alertContent);
      openAlert();

      await delay(2000);
      return updatedFavorite;
    } catch (error: any) {
      const errorMessage =
        error?.message || error?.error?.message || "دوباره تلاش کنید";

      alertView.alertContent.push({
        mode: "danger",
        textBody: errorMessage,
      });

      setAlertView("SNACKBAR", alertView.alertContent);
      openAlert();

      return favorite;
    }
  };

  const addToCart = async (productID: number, sku: string) => {
    return await addItem({
      id: productID,
      sku: sku || "",
    });
  };

  const removeFromCart = async (productID: number, sku: string) => {
    return await removeItem({
      id: productID,
      sku: sku || "",
    });
  };

  const openCouponModal = (data: {
    id: number;
    title: string;
    price: number;
    sku: string | undefined;
    expire?: DetailsExpire;
    type?: COURSE_TYPE;
    slug?: string;
  }) => {
    const { expire, id, title, price, sku, slug, type } = data || {};
    if (!expire) return;
    modalActions.addModal(MODAL_TYPES.COUPON_PRODUCT, {
      config: {
        hideCloseIcon: true,
      },
      data: {
        discount: expire?.value_percent || 0,
        product: title,
        expireDay: expire?.window_end_at_formatted || "",
        price: price || 0,
        productUrl: handleCourseUrl(type, slug || sku),
        id: id.toString(),
        expiryStatus: expire?.expiry_status || EXPIRY_STATUS.EXPIRABLE,
      },
    });
  };

  return { addFavoriteCourse, addToCart, removeFromCart, openCouponModal };
};
